import React, { useEffect, useMemo, useRef } from 'react';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { queryKeys, queryValues } from '~/routes/query-params';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';

import { InsightsDrawer } from './InsightsDrawer';

export const InsightsFullDrawerRender = () => {
  const { params } = useQueryParams();
  const { trackInsightsDrawerOpen } = usePosthogEvents();
  const trackedDrawersRef = useRef<Set<string>>(new Set());

  const entityTagFromParams = params[queryKeys.entityTag];
  const activeDrawer = params[queryKeys.activeDrawer];
  const activeInsightId = params[queryKeys.activeInsightId];
  const isDrawerOpen =
    entityTagFromParams &&
    activeDrawer === queryValues.insightsFullDrawer &&
    activeInsightId;

  const drawerId = useMemo(() => {
    return entityTagFromParams && activeInsightId
      ? `${entityTagFromParams}-${activeInsightId}`
      : null;
  }, [entityTagFromParams, activeInsightId]);

  const utmSource = params[queryKeys.utmSource];

  useEffect(() => {
    if (!isDrawerOpen || !drawerId || !utmSource) {
      if (
        !isDrawerOpen &&
        drawerId &&
        trackedDrawersRef.current.has(drawerId)
      ) {
        trackedDrawersRef.current.delete(drawerId);
      }

      return;
    }

    if (trackedDrawersRef.current.has(drawerId)) {
      return;
    }

    trackedDrawersRef.current.add(drawerId);

    trackInsightsDrawerOpen(utmSource as PosthogTrackingSource, 'full', {});
  }, [isDrawerOpen, drawerId, utmSource, trackInsightsDrawerOpen]);

  if (entityTagFromParams && activeDrawer === queryValues.insightsFullDrawer) {
    return (
      <ErrorBoundaryWrapper showBorder={false}>
        <InsightsDrawer
          key={entityTagFromParams}
          entityTag={entityTagFromParams}
        />
      </ErrorBoundaryWrapper>
    );
  }

  return null;
};
